/**
 * LUXE BIJOUX THEME - MAIN JAVASCRIPT
 * Ultra Luxury Jewelry Theme
 */

(function() {
  'use strict';

  // ===== GLOBAL STATE =====
  const LUXE = {
    initialized: false,
    scrollPosition: 0,
    isScrolling: null
  };

  // ===== DOM READY =====
  function domReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  // ===== INIT AOS (Animate On Scroll) =====
  function initAOS() {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 1000,
        easing: 'ease-out-cubic',
        once: true,
        offset: 100,
        disable: function() {
          return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }
      });
    } else {
      // Load AOS from CDN if not available
      const aosCSS = document.createElement('link');
      aosCSS.rel = 'stylesheet';
      aosCSS.href = 'https://unpkg.com/aos@2.3.1/dist/aos.css';
      document.head.appendChild(aosCSS);

      const aosJS = document.createElement('script');
      aosJS.src = 'https://unpkg.com/aos@2.3.1/dist/aos.js';
      aosJS.onload = function() {
        AOS.init({
          duration: 1000,
          easing: 'ease-out-cubic',
          once: true,
          offset: 100,
          disable: function() {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          }
        });
      };
      document.body.appendChild(aosJS);
    }
  }

  // ===== HEADER SCROLL EFFECTS =====
  function initHeaderScroll() {
    const header = document.querySelector('.luxe-header');
    if (!header) return;

    let lastScroll = 0;

    window.addEventListener('scroll', function() {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }

      lastScroll = currentScroll;
    }, { passive: true });
  }

  // ===== SMOOTH SCROLL FOR ANCHOR LINKS =====
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#' || href === '') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          const headerOffset = 100;
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // ===== CART FUNCTIONALITY =====
  function initCart() {
    const cartLinks = document.querySelectorAll('.cart-link');
    
    cartLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        // Add cart drawer functionality here if needed
      });
    });

    // Update cart count
    updateCartCount();
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const countElements = document.querySelectorAll('#cart-count');
        countElements.forEach(element => {
          element.textContent = cart.item_count;
          if (cart.item_count > 0) {
            element.style.display = 'flex';
          } else {
            element.style.display = 'none';
          }
        });
      })
      .catch(error => console.error('Error updating cart:', error));
  }

  // ===== LOADING OVERLAY =====
  function initLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.innerHTML = '<div class="loading-spinner"></div>';
    document.body.appendChild(overlay);

    window.addEventListener('load', function() {
      setTimeout(function() {
        overlay.classList.add('hidden');
        setTimeout(function() {
          overlay.remove();
        }, 500);
      }, 300);
    });
  }

  // ===== LAZY LOADING IMAGES =====
  function initLazyLoad() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            observer.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  // ===== PARALLAX EFFECT =====
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    if (parallaxElements.length === 0) return;

    window.addEventListener('scroll', function() {
      const scrolled = window.pageYOffset;
      
      parallaxElements.forEach(element => {
        const speed = element.dataset.parallax || 0.5;
        const yPos = -(scrolled * speed);
        element.style.transform = `translateY(${yPos}px)`;
      });
    }, { passive: true });
  }

  // ===== WISHLIST FUNCTIONALITY =====
  function initWishlist() {
    const wishlistButtons = document.querySelectorAll('.wishlist-button');
    
    wishlistButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        this.classList.toggle('active');
        
        // Here you would typically save to localStorage or send to server
        const productId = this.dataset.productId;
        if (this.classList.contains('active')) {
          console.log('Added to wishlist:', productId);
        } else {
          console.log('Removed from wishlist:', productId);
        }
      });
    });
  }

  // ===== PRODUCT IMAGE ZOOM =====
  function initImageZoom() {
    const productImages = document.querySelectorAll('.product-image-wrapper');
    
    productImages.forEach(wrapper => {
      wrapper.addEventListener('mousemove', function(e) {
        const rect = this.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;
        
        const img = this.querySelector('.product-image');
        if (img) {
          img.style.transformOrigin = `${xPercent}% ${yPercent}%`;
        }
      });

      wrapper.addEventListener('mouseleave', function() {
        const img = this.querySelector('.product-image');
        if (img) {
          img.style.transformOrigin = 'center';
        }
      });
    });
  }

  // ===== NEWSLETTER FORM =====
  function initNewsletter() {
    const forms = document.querySelectorAll('.newsletter-form');
    
    forms.forEach(form => {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        
        const email = this.querySelector('input[type="email"]').value;
        const button = this.querySelector('button[type="submit"]');
        const originalText = button.textContent;
        
        button.textContent = 'Envoi...';
        button.disabled = true;
        
        // Simulate API call
        setTimeout(() => {
          button.textContent = '✓ Inscrit!';
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
            this.reset();
          }, 2000);
        }, 1000);
      });
    });
  }

  // ===== SCROLL REVEAL COUNTER ANIMATION =====
  function initCounterAnimation() {
    const counters = document.querySelectorAll('.stat-number');
    
    if (counters.length === 0) return;

    const observerOptions = {
      threshold: 0.5,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
          entry.target.classList.add('counted');
          animateCounter(entry.target);
        }
      });
    }, observerOptions);

    counters.forEach(counter => observer.observe(counter));
  }

  function animateCounter(element) {
    const text = element.textContent;
    const hasPlus = text.includes('+');
    const hasPercent = text.includes('%');
    const number = parseInt(text.replace(/\D/g, ''));
    
    if (isNaN(number)) return;

    const duration = 2000;
    const steps = 60;
    const increment = number / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      current += increment;
      step++;
      
      let display = Math.floor(current);
      if (hasPlus) display += 'K+';
      else if (hasPercent) display += '%';
      
      element.textContent = display;

      if (step >= steps) {
        clearInterval(timer);
        element.textContent = text;
      }
    }, duration / steps);
  }

  // ===== MOBILE MENU TOGGLE =====
  function initMobileMenu() {
    // Create mobile menu button if it doesn't exist
    const header = document.querySelector('.luxe-header');
    if (!header || window.innerWidth > 768) return;

    let menuButton = header.querySelector('.mobile-menu-toggle');
    if (!menuButton) {
      menuButton = document.createElement('button');
      menuButton.className = 'mobile-menu-toggle';
      menuButton.innerHTML = `
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      `;
      header.querySelector('.header-container').prepend(menuButton);
    }

    menuButton.addEventListener('click', function() {
      const nav = header.querySelector('.header-nav');
      if (nav) {
        nav.classList.toggle('active');
        this.classList.toggle('active');
      }
    });
  }

  // ===== INITIALIZE ALL =====
  function init() {
    if (LUXE.initialized) return;

    initLoadingOverlay();
    initAOS();
    initHeaderScroll();
    initSmoothScroll();
    initCart();
    initLazyLoad();
    initParallax();
    initWishlist();
    initImageZoom();
    initNewsletter();
    initCounterAnimation();
    initMobileMenu();

    LUXE.initialized = true;

    console.log('🌟 Luxe Bijoux Theme initialized');
  }

  // ===== START =====
  domReady(init);

  // Re-initialize on Shopify theme editor events
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function() {
      if (typeof AOS !== 'undefined') {
        AOS.refresh();
      }
    });

    document.addEventListener('shopify:section:reorder', function() {
      if (typeof AOS !== 'undefined') {
        AOS.refresh();
      }
    });
  }

  // Expose LUXE object globally if needed
  window.LUXE = LUXE;

})();
